import { useCallback } from 'react';
import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	ApplicationSummary,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { ArrowRightOutlined } from '@ant-design/icons';
import {
	ApplicationPreworkStatus,
	PreworkCompletionStatus,
	PreworkReviewAction,
} from '../../../shared/PreWorkServiceTypes';
import {
	displayPreworkRecommendationStatus,
	displayPreworkStatus,
	getFullName,
	hasPrivilege,
} from '../../../shared/Utils';
import moment from 'moment';
import { DATE_DO_MMM_YYYY } from '../../../shared/Constants';
import useUserSession from '../../../hooks/useUserSession';
import { PrivilegeCodes } from '../../../shared/RoleConstants';

const { Text } = Typography;

interface ApplicationSummaryCardProps extends ApplicationPreworkStatus {
	onEvaluateClick: (application: ApplicationSummary) => void;
}

export const ApplicationSummaryCard = (props: ApplicationSummaryCardProps) => {
	const {
		application,
		preworkCompletionStatus,
		preworkMentorCompletionStatus,
		preworkMentorNextAttemptDate,
		approverPreworkRecommendation,
		approverComment,
		preworkMentor,
		isForwardedToApprover,
		onEvaluateClick,
	} = props;
	const {
		applicant: { srcmid, gender },
		center,
	} = application;
	const { t } = useTranslation();
	const { selectedRole, loggedInUserPrivileges } = useUserSession();
	const isApprover = selectedRole === UserRole.APPROVER;

	const handleEvaluateClick = useCallback(() => {
		onEvaluateClick(application);
	}, [application, onEvaluateClick]);

	const preworkStatus = t(
		displayPreworkStatus(preworkCompletionStatus, preworkMentorCompletionStatus)
	);

	const recommendationStatus = t(
		displayPreworkRecommendationStatus(approverPreworkRecommendation)
	);

	const disableViewButton =
		(preworkMentorNextAttemptDate &&
			moment().isSameOrBefore(preworkMentorNextAttemptDate)) ||
		preworkCompletionStatus !== PreworkCompletionStatus.COMPLETED ||
		!preworkCompletionStatus ||
		(isForwardedToApprover &&
			approverPreworkRecommendation !==
				PreworkReviewAction.EXTEND_TO_PREWORK_MENTOR);

	const resubmitDate = moment(preworkMentorNextAttemptDate).format(
		DATE_DO_MMM_YYYY
	);

	const showInfoIcon =
		disableViewButton &&
		(preworkMentorCompletionStatus === PreworkCompletionStatus.INCOMPLETE ||
			preworkMentorCompletionStatus ===
				PreworkCompletionStatus.PARTIALLY_COMPLETED) &&
		approverPreworkRecommendation !== PreworkReviewAction.PREWORK_APPROVE &&
		approverPreworkRecommendation !== PreworkReviewAction.PREWORK_REJECT;

	const showPreworkMentorEvaluate = hasPrivilege(
		PrivilegeCodes.CAPTURE_PREWORK_MENTOR_ANSWERS,
		loggedInUserPrivileges
	);

	return (
		<>
			<div className="flex flex-col rounded-lg border border-solid border-gray-300 bg-card-bg p-[16px] py-[24px]">
				<div className="flex flex-row gap-6 mb-4">
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('fullName')}</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{getFullName(application.applicant)}
						</Text>
					</div>
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">{t('idCardNo')}</Text>
						<Text className="text-gray-650 text-sm font-bold">{srcmid}</Text>
					</div>
				</div>

				<div className="flex flex-row gap-6 mb-4">
					<div className="flex flex-col gap-1 w-[50%]">
						<Text className="text-light-gray text-sm">
							{t('approverStatus')}
						</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{approverPreworkRecommendation ? recommendationStatus : 'N/A'}
						</Text>
					</div>
					{!isApprover && (
						<div className="flex flex-col gap-1 w-[50%]">
							<Text className="text-light-gray text-sm">
								{t('preworkStatus')}
							</Text>
							<Text className="text-gray-650 text-sm font-bold">
								{preworkStatus}
							</Text>
						</div>
					)}
					{isApprover && (
						<div className="flex flex-col gap-1 w-[50%]">
							<Text className="text-light-gray text-sm">{t('gender')}</Text>
							<Text className="text-gray-650 text-sm font-bold">
								{gender &&
									gender.charAt(0).toUpperCase() +
										gender.slice(1).toLowerCase()}
							</Text>
						</div>
					)}
				</div>

				{isApprover && (
					<div className="flex flex-row gap-6 mb-4">
						<div className="flex flex-col gap-1 w-[50%]">
							<Text className="text-light-gray text-sm">
								{t('preworkMentor')}
							</Text>
							<Text className="text-gray-650 text-sm font-bold">
								{getFullName(preworkMentor) || '-'}
							</Text>
						</div>
						<div className="flex flex-col gap-1 w-[50%]">
							<Text className="text-light-gray text-sm">
								{t('mentorSrcmId')}
							</Text>
							<Text className="text-gray-650 text-sm font-bold">
								{preworkMentor?.srcmId || '-'}
							</Text>
						</div>
					</div>
				)}

				<div className="flex flex-row gap-6 mb-4">
					{!isApprover && (
						<div className="flex flex-col gap-1 w-[50%]">
							<Text className="text-light-gray text-sm">{t('gender')}</Text>
							<Text className="text-gray-650 text-sm font-bold">
								{gender &&
									gender.charAt(0).toUpperCase() +
										gender.slice(1).toLowerCase()}
							</Text>
						</div>
					)}
					<div
						className={`flex flex-col gap-1 ${
							isApprover ? 'w-[100%]' : 'w-[50%]'
						}`}
					>
						<Text className="text-light-gray text-sm">{t('zone')}</Text>
						<Text className="text-gray-650 text-sm font-bold">
							{center.zone}
						</Text>
					</div>
				</div>

				{!isApprover && (
					<div className="flex flex-row gap-6 mb-4">
						<div className="flex flex-col gap-1 w-[100%]">
							<Text className="text-light-gray text-sm">
								{t('approverComments')}
							</Text>
							<Text className="text-gray-650 text-sm font-bold">
								{approverComment || 'N/A'}
							</Text>
						</div>
					</div>
				)}

				{isApprover ? (
					<div className="flex flex-col justify-center items-center gap-1">
						<Button
							type="primary"
							onClick={handleEvaluateClick}
							className="w-full"
						>
							<span className="flex items-center justify-center gap-1">
								{approverPreworkRecommendation === PreworkReviewAction.PENDING
									? t('review')
									: t('view')}{' '}
							</span>
						</Button>
					</div>
				) : (
					<div className="flex flex-col justify-center items-center gap-1">
						{showPreworkMentorEvaluate && (
							<>
								<Button
									type="primary"
									onClick={handleEvaluateClick}
									className="w-full"
									disabled={disableViewButton}
								>
									<span className="flex items-center justify-center gap-1">
										{preworkMentorCompletionStatus ===
										PreworkCompletionStatus.COMPLETED
											? t('view')
											: t('review')}{' '}
										<ArrowRightOutlined />
									</span>
								</Button>
								{showInfoIcon && (
									<Text className="text-light-gray text-sm">
										{t('resubmitAfter', { resubmitDate })}
									</Text>
								)}
							</>
						)}
					</div>
				)}
			</div>
		</>
	);
};
